import { BaseGenerator } from './BaseGenerator.js';
import { DensitySampler } from './DensitySampler.js';
import { ObjectPlacer } from './ObjectPlacer.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Forest map generator.
 * Produces outdoor maps with grass terrain, trees, paths, clearings, and water features.
 *
 * Supports three quality modes:
 * - "draft"    — direct tile ID output (legacy, fast)
 * - "standard" — pipeline with transition + cleanup passes
 * - "high"     — pipeline + WFC resolver + full decoration
 */
export class ForestGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';

    if (quality === 'draft') {
      return this._generateDraft(content);
    }

    return this._generatePipeline(content, quality);
  }

  // ---- Pipeline mode (standard / high) ----

  _generatePipeline(content, quality) {
    const tiles = content.tiles || ForestGenerator.DEFAULT_TILES;
    const opts = { ...ForestGenerator.DEFAULT_OPTIONS, ...this.options };

    // 1. Structure pass — output zone map
    const ground = this.createGrid(this.width, this.height, ZONE.GRASS);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const zones = this.createGrid(this.width, this.height, ZONE.GRASS);

    // Terrain variation patches (dirt, different grass)
    this._structureTerrainVariation(ground, zones, opts);

    // Water features
    if (opts.water) {
      this._structureWater(ground, zones, opts);
    }

    // Paths
    if (opts.paths) {
      this._structurePaths(ground, zones, opts);
    }

    // Clearings
    const clearings = this._generateClearings(ground, tiles, opts);

    // Smooth paths before tree placement
    if (opts.paths) {
      this._smoothPaths(ground, ZONE.PATH, ZONE.GRASS);
    }

    // Trees on object layer
    this._structureTrees(objects, overhead, ground, opts, clearings);

    // 2. Run pipeline
    const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });

    if (quality !== 'high') {
      pipeline.removePass('decoration');
    }

    const pipelineOptions = {
      tileMapping: this._buildTileMapping(tiles),
      floorTileIds: [ZONE.GRASS, ZONE.DIRT, ZONE.PATH],
      corridorTileId: ZONE.PATH,
    };

    if (quality === 'high') {
      pipelineOptions.decorations = this._buildDecorationSpecs(tiles, opts);
    }

    const result = pipeline.run({
      ground,
      objects,
      overhead,
      zones,
      width: this.width,
      height: this.height,
      metadata: { clearings },
    }, pipelineOptions);

    // 3. Map zones → final tile IDs
    const finalGround = this._resolveZones(result.ground, tiles);
    const finalObjects = this._resolveObjectZones(result.objects, tiles);
    const finalOverhead = this._resolveObjectZones(result.overhead, tiles);

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(finalGround) },
        { name: 'Objects', data: this.flattenGrid(finalObjects) },
        { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
      ],
      tilesets: content.tilesets || [],
    });
  }

  _structureTerrainVariation(ground, zones, opts) {
    const patchCount = Math.floor(this.width * this.height * 0.01 * (opts.terrainVariation || 5));

    for (let i = 0; i < patchCount; i++) {
      const cx = this.rng.nextInt(0, this.width - 1);
      const cy = this.rng.nextInt(0, this.height - 1);
      const radius = this.rng.nextInt(2, 6);
      const zone = this.rng.chance(0.3) ? ZONE.DIRT : ZONE.GRASS;

      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (dx * dx + dy * dy <= radius * radius && this.rng.chance(0.7)) {
            this.setCell(ground, cx + dx, cy + dy, zone);
            this.setCell(zones, cx + dx, cy + dy, zone);
          }
        }
      }
    }
  }

  _structureWater(ground, zones, opts) {
    const count = opts.waterFeatures || this.rng.nextInt(1, 3);
    for (let i = 0; i < count; i++) {
      const cx = this.rng.nextInt(5, this.width - 6);
      const cy = this.rng.nextInt(5, this.height - 6);

      if (this.rng.chance(0.5)) {
        // Pond
        const rx = this.rng.nextInt(3, 7);
        const ry = this.rng.nextInt(2, 5);
        for (let dy = -ry - 1; dy <= ry + 1; dy++) {
          for (let dx = -rx - 1; dx <= rx + 1; dx++) {
            const dist = (dx * dx) / ((rx + 0.5) * (rx + 0.5)) + (dy * dy) / ((ry + 0.5) * (ry + 0.5));
            if (dist <= 1.0) {
              this.setCell(ground, cx + dx, cy + dy, ZONE.WATER);
              this.setCell(zones, cx + dx, cy + dy, ZONE.WATER);
            } else if (dist <= 1.4) {
              this.setCell(ground, cx + dx, cy + dy, ZONE.SAND);
              this.setCell(zones, cx + dx, cy + dy, ZONE.SAND);
            }
          }
        }
      } else {
        // Stream
        let sx = this.rng.chance(0.5) ? 0 : this.rng.nextInt(0, this.width - 1);
        let sy = this.rng.chance(0.5) ? 0 : this.rng.nextInt(0, this.height - 1);
        const targetX = this.width - 1;
        const targetY = this.height - 1;

        for (let step = 0; step < this.width + this.height; step++) {
          this.setCell(ground, sx, sy, ZONE.WATER);
          this.setCell(zones, sx, sy, ZONE.WATER);
          if (this.rng.chance(0.6)) sx += sx < targetX ? 1 : -1;
          else sy += sy < targetY ? 1 : -1;
          sx += this.rng.nextInt(-1, 1);
          sx = Math.max(0, Math.min(this.width - 1, sx));
          sy = Math.max(0, Math.min(this.height - 1, sy));
        }
      }
    }
  }

  _structurePaths(ground, zones, opts) {
    const pathCount = opts.pathCount || this.rng.nextInt(1, 3);
    for (let i = 0; i < pathCount; i++) {
      const start = this._randomEdgePoint();
      const end = this._randomEdgePoint();
      this._drawWanderingZonePath(ground, zones, start.x, start.y, end.x, end.y, ZONE.PATH, opts.pathWidth || 2);
    }
  }

  _drawWanderingZonePath(ground, zones, x0, y0, x1, y1, zone, width) {
    let cx = x0, cy = y0;
    const maxSteps = (this.width + this.height) * 2;

    for (let step = 0; step < maxSteps; step++) {
      const half = Math.floor(width / 2);
      for (let dy = -half; dy <= half; dy++) {
        for (let dx = -half; dx <= half; dx++) {
          this.setCell(ground, cx + dx, cy + dy, zone);
          this.setCell(zones, cx + dx, cy + dy, zone);
        }
      }
      if (cx === x1 && cy === y1) break;
      if (this.rng.chance(0.7)) {
        if (cx !== x1) cx += cx < x1 ? 1 : -1;
        if (cy !== y1) cy += cy < y1 ? 1 : -1;
      } else {
        cx += this.rng.nextInt(-1, 1);
        cy += this.rng.nextInt(-1, 1);
      }
      cx = Math.max(0, Math.min(this.width - 1, cx));
      cy = Math.max(0, Math.min(this.height - 1, cy));
    }
  }

  _smoothPaths(ground, pathZone, grassZone) {
    // Widen paths and fill jagged corners
    const snapshot = ground.map(row => [...row]);
    for (let y = 1; y < this.height - 1; y++) {
      for (let x = 1; x < this.width - 1; x++) {
        if (snapshot[y][x] !== pathZone) continue;
        // Fill diagonal gaps: if two perpendicular neighbors are path, fill corner
        for (const [dx, dy] of [[1,0],[0,1],[-1,0],[0,-1]]) {
          const nx = x + dx, ny = y + dy;
          if (this.getCell(snapshot, nx, ny) === grassZone && this.rng.chance(0.4)) {
            const pathNeighbors = this.countNeighbors(snapshot, nx, ny, v => v === pathZone, false);
            if (pathNeighbors >= 2) {
              this.setCell(ground, nx, ny, pathZone);
            }
          }
        }
      }
    }
  }

  _structureTrees(objects, overhead, ground, opts, clearings) {
    const treeDensity = new DensitySampler(
      opts.treeDensityCurve || 'uniform',
      opts.treeDensityMin || 0.15,
      opts.treeDensityMax || 0.4
    );

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (ground[y][x] !== ZONE.GRASS && ground[y][x] !== ZONE.DIRT) continue;

        let inClearing = false;
        for (const c of clearings) {
          if ((x - c.x) ** 2 + (y - c.y) ** 2 < c.radius ** 2) {
            inClearing = true;
            break;
          }
        }
        if (inClearing) continue;

        if (!treeDensity.test(x / this.width, y / this.height, this.rng)) continue;

        // Skip if adjacent tree exists (70% chance) for natural spacing
        const adjacentTrees = this.countNeighbors(objects, x, y, v => v === ZONE.TREE, false);
        if (adjacentTrees > 0 && this.rng.chance(0.7)) continue;

        this.setCell(objects, x, y, ZONE.TREE);
        if (y > 0) {
          this.setCell(overhead, x, y - 1, ZONE.TREE);
        }
      }
    }
  }

  _buildTileMapping(tiles) {
    // Minimal mapping: zone → { center }
    // Transition tiles can be added when the user provides a full tileset
    return {
      [ZONE.GRASS]: { center: tiles.grass },
      [ZONE.DIRT]:  { center: tiles.grassVariants?.[1] || tiles.grass },
      [ZONE.WATER]: { center: tiles.water },
      [ZONE.SAND]:  { center: tiles.waterEdge || tiles.grass },
      [ZONE.PATH]:  { center: tiles.path },
    };
  }

  _buildDecorationSpecs(tiles, opts) {
    const decoTiles = tiles.decorations || [];
    if (decoTiles.length === 0) return [];

    return decoTiles.map((tileId, index) => ({
      tileId,
      density: index === 0 ? 0.012 : 0.008,
      spacing: 3,
      validGround: [tiles.grass, ...(tiles.grassVariants || [])],
      avoidEdges: false,
    }));
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.GRASS]: tiles.grass,
      [ZONE.DIRT]:  tiles.grassVariants?.[1] || tiles.grass,
      [ZONE.WATER]: tiles.water,
      [ZONE.SAND]:  tiles.waterEdge || tiles.grass,
      [ZONE.PATH]:  tiles.path,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  _resolveObjectZones(grid, tiles) {
    const treeTiles = tiles.trees || [tiles.tree];
    return grid.map(row => row.map(z => {
      if (z === ZONE.TREE) return this.rng.pick(treeTiles);
      if (z === ZONE.BUSH) return tiles.decorations?.[0] || 0;
      if (z === ZONE.FLOWER) return tiles.decorations?.[1] || 0;
      return z;
    }));
  }

  // ---- Draft mode (original direct tile output) ----

  _generateDraft(content) {
    const tiles = content.tiles || ForestGenerator.DEFAULT_TILES;
    const opts = { ...ForestGenerator.DEFAULT_OPTIONS, ...this.options };

    const ground = this.createGrid(this.width, this.height, tiles.grass);
    const objects = this.createGrid(this.width, this.height, 0);
    const overhead = this.createGrid(this.width, this.height, 0);

    this._generateTerrainVariation(ground, tiles, opts);
    if (opts.water) this._generateWater(ground, tiles, opts);
    if (opts.paths) this._generatePaths(ground, tiles, opts);
    if (opts.paths) this._smoothPaths(ground, tiles.path, tiles.grass);
    const clearings = this._generateClearings(ground, tiles, opts);
    this._generateTrees(objects, overhead, ground, tiles, opts, clearings);
    this._generateDecorations(objects, ground, tiles, opts, clearings);

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground, objects, overhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(ground) },
        { name: 'Objects', data: this.flattenGrid(objects) },
        { name: 'Overhead', data: this.flattenGrid(overhead) },
      ],
      tilesets: content.tilesets || [],
    });
  }

  // ---- Original helpers (used by draft mode) ----

  _generateTerrainVariation(ground, tiles, opts) {
    const patchCount = Math.floor(this.width * this.height * 0.01 * (opts.terrainVariation || 5));
    const variants = tiles.grassVariants || [tiles.grass];

    for (let i = 0; i < patchCount; i++) {
      const cx = this.rng.nextInt(0, this.width - 1);
      const cy = this.rng.nextInt(0, this.height - 1);
      const radius = this.rng.nextInt(2, 6);
      const variant = this.rng.pick(variants);

      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (dx * dx + dy * dy <= radius * radius && this.rng.chance(0.7)) {
            this.setCell(ground, cx + dx, cy + dy, variant);
          }
        }
      }
    }
  }

  _generateWater(ground, tiles, opts) {
    const count = opts.waterFeatures || this.rng.nextInt(1, 3);
    for (let i = 0; i < count; i++) {
      const cx = this.rng.nextInt(5, this.width - 6);
      const cy = this.rng.nextInt(5, this.height - 6);

      if (this.rng.chance(0.5)) {
        const rx = this.rng.nextInt(3, 7);
        const ry = this.rng.nextInt(2, 5);
        for (let dy = -ry - 1; dy <= ry + 1; dy++) {
          for (let dx = -rx - 1; dx <= rx + 1; dx++) {
            const dist = (dx * dx) / ((rx + 0.5) * (rx + 0.5)) + (dy * dy) / ((ry + 0.5) * (ry + 0.5));
            if (dist <= 1.0) {
              this.setCell(ground, cx + dx, cy + dy, tiles.water);
            } else if (dist <= 1.4 && tiles.waterEdge) {
              this.setCell(ground, cx + dx, cy + dy, tiles.waterEdge);
            }
          }
        }
      } else {
        let sx = this.rng.chance(0.5) ? 0 : this.rng.nextInt(0, this.width - 1);
        let sy = this.rng.chance(0.5) ? 0 : this.rng.nextInt(0, this.height - 1);
        const targetX = this.width - 1;
        const targetY = this.height - 1;

        for (let step = 0; step < this.width + this.height; step++) {
          this.setCell(ground, sx, sy, tiles.water);
          if (tiles.waterEdge) {
            this.setCell(ground, sx - 1, sy, tiles.waterEdge);
            this.setCell(ground, sx + 1, sy, tiles.waterEdge);
          }
          if (this.rng.chance(0.6)) sx += sx < targetX ? 1 : -1;
          else sy += sy < targetY ? 1 : -1;
          sx += this.rng.nextInt(-1, 1);
          sx = Math.max(0, Math.min(this.width - 1, sx));
          sy = Math.max(0, Math.min(this.height - 1, sy));
        }
      }
    }
  }

  _generatePaths(ground, tiles, opts) {
    const pathCount = opts.pathCount || this.rng.nextInt(1, 3);
    for (let i = 0; i < pathCount; i++) {
      const start = this._randomEdgePoint();
      const end = this._randomEdgePoint();
      this._drawWanderingPath(ground, start.x, start.y, end.x, end.y, tiles.path, opts.pathWidth || 2);
    }
  }

  _randomEdgePoint() {
    const side = this.rng.nextInt(0, 3);
    switch (side) {
      case 0: return { x: this.rng.nextInt(0, this.width - 1), y: 0 };
      case 1: return { x: this.rng.nextInt(0, this.width - 1), y: this.height - 1 };
      case 2: return { x: 0, y: this.rng.nextInt(0, this.height - 1) };
      case 3: return { x: this.width - 1, y: this.rng.nextInt(0, this.height - 1) };
    }
  }

  _drawWanderingPath(grid, x0, y0, x1, y1, tile, width) {
    let cx = x0, cy = y0;
    const maxSteps = (this.width + this.height) * 2;

    for (let step = 0; step < maxSteps; step++) {
      const half = Math.floor(width / 2);
      for (let dy = -half; dy <= half; dy++) {
        for (let dx = -half; dx <= half; dx++) {
          this.setCell(grid, cx + dx, cy + dy, tile);
        }
      }
      if (cx === x1 && cy === y1) break;
      if (this.rng.chance(0.7)) {
        if (cx !== x1) cx += cx < x1 ? 1 : -1;
        if (cy !== y1) cy += cy < y1 ? 1 : -1;
      } else {
        cx += this.rng.nextInt(-1, 1);
        cy += this.rng.nextInt(-1, 1);
      }
      cx = Math.max(0, Math.min(this.width - 1, cx));
      cy = Math.max(0, Math.min(this.height - 1, cy));
    }
  }

  _generateClearings(ground, tiles, opts) {
    const count = opts.clearings || this.rng.nextInt(1, 4);
    const clearings = [];

    for (let i = 0; i < count; i++) {
      const cx = this.rng.nextInt(8, this.width - 9);
      const cy = this.rng.nextInt(8, this.height - 9);
      const radius = this.rng.nextInt(3, 6);
      clearings.push({ x: cx, y: cy, radius });
    }

    return clearings;
  }

  _generateTrees(objects, overhead, ground, tiles, opts, clearings) {
    const treeDensity = new DensitySampler(
      opts.treeDensityCurve || 'uniform',
      opts.treeDensityMin || 0.15,
      opts.treeDensityMax || 0.4
    );

    const treeTiles = tiles.trees || [tiles.tree];
    const treeTopTiles = tiles.treeTops || [tiles.treeTop];

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (ground[y][x] !== tiles.grass && !(tiles.grassVariants || []).includes(ground[y][x])) continue;

        let inClearing = false;
        for (const c of clearings) {
          if ((x - c.x) ** 2 + (y - c.y) ** 2 < c.radius ** 2) {
            inClearing = true;
            break;
          }
        }
        if (inClearing) continue;

        const nx = x / this.width;
        const ny = y / this.height;
        if (!treeDensity.test(nx, ny, this.rng)) continue;

        // Skip if adjacent tree exists (70% chance) for natural spacing
        const adjacentTrees = this.countNeighbors(objects, x, y, v => v !== 0, false);
        if (adjacentTrees > 0 && this.rng.chance(0.7)) continue;

        const trunk = this.rng.pick(treeTiles);
        const top = this.rng.pick(treeTopTiles);
        this.setCell(objects, x, y, trunk);
        if (top && y > 0) {
          this.setCell(overhead, x, y - 1, top);
        }
      }
    }
  }

  _generateDecorations(objects, ground, tiles, opts, clearings) {
    const decoTiles = tiles.decorations || [];
    if (decoTiles.length === 0) return;

    const count = Math.floor(this.width * this.height * (opts.decorationDensity || 0.04));
    for (let i = 0; i < count; i++) {
      const x = this.rng.nextInt(0, this.width - 1);
      const y = this.rng.nextInt(0, this.height - 1);

      if (objects[y][x] !== 0) continue;
      if (ground[y][x] !== tiles.grass && !(tiles.grassVariants || []).includes(ground[y][x])) continue;

      this.setCell(objects, x, y, this.rng.pick(decoTiles));
    }
  }

  static DEFAULT_TILES = {
    grass: 1,
    grassVariants: [1, 2, 3],
    water: 10,
    waterEdge: 11,
    path: 20,
    tree: 30,
    treeTop: 31,
    trees: [30, 32, 33],
    treeTops: [31, 34, 35],
    decorations: [40, 41, 42, 43, 44],
  };

  static DEFAULT_OPTIONS = {
    water: true,
    waterFeatures: 2,
    paths: true,
    pathCount: 2,
    pathWidth: 2,
    clearings: 3,
    treeDensityCurve: 'uniform',
    treeDensityMin: 0.15,
    treeDensityMax: 0.35,
    terrainVariation: 5,
    decorationDensity: 0.04,
  };

  static getEmittedZones() {
    return [
      ZONE.GRASS, ZONE.DIRT, ZONE.PATH, ZONE.SAND, ZONE.WATER, ZONE.TREE, ZONE.BUSH, ZONE.FLOWER,
    ];
  }
}
